import { forwardRef, useImperativeHandle, useEffect, useId, useRef } from 'react';
import className from 'classnames/bind';
import { useInView } from 'motion/react';

import styles from './YoutubeEmbed.module.scss';
import { useYoutubeVideo } from '@/providers/YoutubeVideoContext';
import { useSoundContext } from '@/providers/SoundContext';
import { useVideoPrivacy } from '@/providers/VideoPrivacyContext';
import VimeoEmbed from '../VimeoEmbed/VimeoEmbed';
import ConsentGate from '../ConsentGate/ConsentGate';
const cx = className.bind(styles);

interface YoutubeEmbedProps {
  id: string;
  autoplay?: boolean;
  soundEnabled?: boolean;
  showControls?: boolean;
  consentSmall?: boolean;
  className?: string;
  customPlayerVars?: Record<string, number | string>; // Allow custom YouTube player parameters
  blockMouseInteraction?: boolean; // Block mouse clicks/hover on the video player
  onStateChange?: (event: YT.OnStateChangeEvent) => void; // Custom state change handler
  onConsentedElsewhere?: () => void; // Another embed's gate granted consent; hand control back to the placeholder
}
export type YTRef = {
  getPlayer: () => VideoPlayer | null;
}

const YoutubeEmbed = forwardRef<YTRef, YoutubeEmbedProps>(({ id, autoplay = true, showControls = true, className, consentSmall, customPlayerVars = {}, blockMouseInteraction = false, onStateChange, onConsentedElsewhere, soundEnabled }, ref) => {
  let youtubeApi: typeof YT | null = null;
  const youtubeRef = useRef<VideoPlayer>(null);
  const { video, setCurrentVideo } = useYoutubeVideo();
  const { globalVolume } = useSoundContext();
  const { mode, consented, granterToken } = useVideoPrivacy();
  const consentToken = useId();
  const gated = mode === 'consent' && !consented;

  const wasGated = useRef(gated);
  if (gated) wasGated.current = true;
  const ungatedByOther = wasGated.current && !gated && granterToken !== consentToken;
  const shouldAutoplay = autoplay && !ungatedByOther;
  const revertsToPlaceholder = ungatedByOther && Boolean(onConsentedElsewhere);

  const playbackRef = useRef<VideoPlayer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { amount: 0.5 });
  useImperativeHandle(ref, () => {
    return {
      getPlayer: () => youtubeRef.current,
    };
  });

  const checkYoutubeLoaded = () => {
    if (typeof window !== 'undefined' && window.YT && window.YT.Player) {
      createYoutubePlayer();
    } else {
      (window.YT as any).ready(createYoutubePlayer);
    }
  }

  const createYoutubePlayer = () => {
    if (typeof window !== 'undefined') {
      youtubeApi = window.YT;
    }
    if (youtubeApi && containerRef.current) {
      try {
        new youtubeApi.Player(containerRef.current, {
          videoId: id,
          playerVars: {
            autoplay: shouldAutoplay ? 1 : 0,
            mute: shouldAutoplay && soundEnabled === false ? 1 : 0, // Only mute if soundEnabled is explicitly false
            controls: showControls ? 1 : 0, // Hide player controls
            rel: 0, // Don't show related videos (only from same channel if any)
            showinfo: 0, // Hide video title and uploader (deprecated but still works)
            playsinline: 1, // Play inline on mobile
            modestbranding: 1, // Hide YouTube logo
            iv_load_policy: 3, // Hide video annotations
            disablekb: 1, // Disable keyboard controls
            fs: 0, // Hide fullscreen button
            cc_load_policy: 0, // Don't show captions by default
            autohide: 1, // Hide controls when playing
            color: 'white', // White progress bar (instead of red)
            enablejsapi: 1, // Enable JavaScript API
            origin: typeof window !== 'undefined' ? window.location.origin : undefined, // Security requirement
            ...customPlayerVars, // Allow override with custom parameters
          },
          host: 'https://www.youtube-nocookie.com',
          events: {
            onReady: (e) => {
              youtubeRef.current = e.target;

              // Set initial volume from global volume setting
              try {
                const youtubeVolume = Math.round(globalVolume * 100);
                e.target.setVolume(youtubeVolume);
              } catch (error) {
                console.warn('Failed to set initial YouTube volume:', error);
              }

              if (shouldAutoplay) {
                if (playbackRef.current && playbackRef.current !== youtubeRef.current) {
                  playbackRef.current.pauseVideo();
                }
                youtubeRef.current.playVideo();
                setCurrentVideo(youtubeRef.current);
              }
            },
            onStateChange: (e) => {
              if (e.data === YT.PlayerState.PLAYING) {
                if (playbackRef.current && playbackRef.current !== e.target) playbackRef.current.pauseVideo();
                setCurrentVideo(e.target);
              } else if (e.data === YT.PlayerState.ENDED || e.data === YT.PlayerState.PAUSED) {
                setCurrentVideo(null);
              }
              // Call custom handler if provided
              if (onStateChange) {
                onStateChange(e);
              }
            }
          }
        });
      } catch (error) {
        console.groupCollapsed('YouTube Embed Error');
        console.warn(error);
        console.warn('Youtube Video ID:', id);
        console.groupEnd();
      }
    }
  }
  useEffect(() => {
    if (video && video !== youtubeRef.current) playbackRef.current = video;
  }, [video]);

  useEffect(() => {
    if (revertsToPlaceholder) onConsentedElsewhere?.();
  }, [revertsToPlaceholder]);

  useEffect(() => {
    if (mode === 'vimeo' || gated || revertsToPlaceholder) return;
    if (typeof window !== 'undefined' && window.YT && window.YT.Player) {
      checkYoutubeLoaded();
      return;
    }
    if (!youtubeApi) {
      document.createElement('script');
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.onload = checkYoutubeLoaded;
      document.body.appendChild(tag);
    } else {
      checkYoutubeLoaded();
    }
  }, [containerRef, mode, gated, revertsToPlaceholder]);

  useEffect(() => {
    if (!inView) {
      if (youtubeRef.current && youtubeRef.current.getPlayerState() === YT.PlayerState.PLAYING) {
        youtubeRef.current.pauseVideo();
      }
    }
  }, [inView]);

  useEffect(() => {
    if (youtubeRef.current && soundEnabled !== undefined) {
      if (soundEnabled) youtubeRef.current.unMute();
      else youtubeRef.current.mute();
    }
  }, [soundEnabled]);

  // Update YouTube player volume when global volume changes
  useEffect(() => {
    if (youtubeRef.current) {
      try {
        // YouTube API expects volume 0-100, we store 0-1
        const youtubeVolume = Math.round(globalVolume * 100);
        youtubeRef.current.setVolume(youtubeVolume);
      } catch (error) {
        console.warn('Failed to set YouTube player volume:', error);
      }
    }
  }, [globalVolume]);

  return (
    <div
      className={cx([
        'youtube-embed',
        className,
        { 'block-interaction': blockMouseInteraction }
      ])}
      ref={rootRef}
    >
      {mode === 'vimeo' ? (
        <VimeoEmbed autoplay={autoplay} soundEnabled={soundEnabled} />
      ) : (
        <>
          <div ref={containerRef} />
          {gated && <ConsentGate youtubeId={id} consentSmall={consentSmall} consentToken={consentToken} />}
        </>
      )}
    </div>
  )
})

export default YoutubeEmbed;